// src/components/SkeletonList.jsx
//
// Drop-in row skeletons for list/table pages. Replaces the
// "blank → spinner → data" sequence with a perceivable
// "blank → placeholder rows → data" sequence, which feels faster even
// when the round-trip time is unchanged.
//
// Usage:
//   <SkeletonList rows={6} columns={5} />
//   <SkeletonList rows={4} variant="cards" />

import React from 'react';
import { Box, Paper, Skeleton, Stack, keyframes } from '@mui/material';

// Subtle shimmer overlay so skeletons read as "loading" rather than
// "placeholder content". Pairs with MUI's own Skeleton `wave` animation
// but adds a soft highlight sweep across the whole block.
const shimmer = keyframes`
  0%   { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const withShimmer = (theme) => ({
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: `linear-gradient(90deg, transparent 0%, ${theme.palette.action.hover} 50%, transparent 100%)`,
    backgroundSize: '200px 100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '-200px 0',
    animation: `${shimmer} 1.6s linear infinite`,
    pointerEvents: 'none',
    zIndex: 1,
    '@media (prefers-reduced-motion: reduce)': { display: 'none' },
  },
});

const SkeletonRow = ({ columns }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: 2,
      p: 1.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      alignItems: 'center',
    }}
  >
    {Array.from({ length: columns }).map((_, i) => (
      <Skeleton key={i} variant="text" width={i === 0 ? '60%' : '80%'} height={20} />
    ))}
  </Box>
);

const SkeletonCard = () => (
  <Paper sx={{ p: 1.75 }}>
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Skeleton variant="circular" width={32} height={32} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="70%" height={20} />
          <Skeleton variant="text" width="40%" height={14} />
        </Box>
      </Stack>
      <Skeleton variant="rounded" width="100%" height={46} />
      <Stack direction="row" spacing={1}>
        <Skeleton variant="rounded" width={64} height={22} />
        <Skeleton variant="rounded" width={80} height={22} />
      </Stack>
    </Stack>
  </Paper>
);

const SkeletonList = ({ rows = 5, columns = 5, variant = 'rows' }) => {
  if (variant === 'cards') {
    return (
      <Stack spacing={1.5} sx={(theme) => withShimmer(theme)}>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </Stack>
    );
  }
  return (
    <Paper sx={(theme) => withShimmer(theme)}>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonRow key={i} columns={columns} />
      ))}
    </Paper>
  );
};

export default SkeletonList;
